import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Loader2 } from 'lucide-react'
import {
  QUIZ_TITLE,
  QUIZ_SUBTITLE,
  QUESTIONS,
  calculateRawScore,
  calculateProtectionScore,
  getArchetype,
  type Archetype,
} from '../data/quizData'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { ArchetypeReport } from '../components/quiz/ArchetypeReport'
import { notifyLeadByEmail } from '../lib/notifyLead'

const colors = {
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  cream: '#F6F6F1',
  nearBlack: '#0F0F0F',
}

const IMG_BASE =
  'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/'
const LOGO_URL = `${IMG_BASE}hudson-stacked-dark.png`

const ease = [0.16, 1, 0.3, 1]

type Step = 'intro' | 'questions' | 'lead' | 'calculating' | 'results'

const QuizFunnelPage: React.FC = () => {
  const [step, setStep] = useState<Step>('intro')
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [lead, setLead] = useState({ name: '', email: '' })
  const [direction, setDirection] = useState(1)
  const [skipLead, setSkipLead] = useState(false)
  const reportNotifiedRef = useRef(false)

  const currentQuestion = QUESTIONS[questionIndex]
  const totalQuestions = QUESTIONS.length
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const hasLeadInput = lead.name.trim() !== '' && lead.email.trim() !== ''
  const isEmailValid = lead.email.trim() === '' || emailPattern.test(lead.email.trim())
  const canSubmit = hasLeadInput && isEmailValid

  const rawScore = calculateRawScore(answers)
  const protectionScore = calculateProtectionScore(rawScore)
  const archetype = getArchetype(rawScore)

  const handleStart = () => setStep('questions')

  const handleSelectOption = useCallback(
    (questionId: string, points: number) => {
      setAnswers((prev) => ({ ...prev, [questionId]: points }))
      setTimeout(() => {
        if (questionIndex < totalQuestions - 1) {
          setDirection(1)
          setQuestionIndex((i) => i + 1)
        } else {
          setStep('lead')
        }
      }, 400)
    },
    [questionIndex, totalQuestions],
  )

  const handleLeadSubmit = () => {
    if (!canSubmit) return
    setStep('calculating')
    setTimeout(() => setStep('results'), 2500)
  }

  const handleSkipLead = () => {
    setSkipLead(true)
    setStep('calculating')
    setTimeout(() => setStep('results'), 2500)
  }

  useEffect(() => {
    if (step !== 'results' || reportNotifiedRef.current) return
    reportNotifiedRef.current = true
    // Fire-and-forget: notify the Hudson team + homeowner via Supabase Edge Function
    // (Resend internal email + GHL contact creation + confirmation email to quiz taker).
    // Wrapped in a no-op .catch so a transient failure never throws a visible error to the user.
    void notifyLeadByEmail({
      source: skipLead ? 'archetype_quiz_no_info' : 'archetype_quiz',
      full_name: lead.name || undefined,
      email: lead.email || undefined,
      payload: {
        rawScore,
        protectionScore,
        archetype: archetype.name,
        archetypeId: archetype.id,
      },
    }).catch((e) => {
      console.warn('[QuizFunnel] notifyLeadByEmail failed (silent):', e)
    })
  }, [step, lead.name, lead.email, rawScore, protectionScore, archetype.name, archetype.id, skipLead])

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? -80 : 80, opacity: 0 }),
  }

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -24 },
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.cream }}>
      <MetaTags
        title="Homeowner Assessment | Hudson My Home"
        description="Discover your homeowner archetype with our 60-second assessment. Get personalized insights about your home management style."
      />
      {step === 'results' ? (
        <Navbar />
      ) : (
        <header className="w-full py-5 px-6">
          <div className="max-w-5xl mx-auto flex justify-center">
            <Link to="/">
              <img src={LOGO_URL} alt="Hudson My Home" className="h-10 w-auto" />
            </Link>
          </div>
        </header>
      )}

      <main className="flex-1">
        <AnimatePresence mode="wait" custom={direction}>
          {/* ─── STEP 1: INTRO ─── */}
          {step === 'intro' && (
            <motion.div
              key="intro"
              {...fadeUp}
              transition={{ duration: 0.6, ease }}
              className="max-w-2xl mx-auto px-6 py-12 lg:py-20 text-center"
            >
              <p
                className="text-sm uppercase tracking-[0.2em] font-medium mb-4"
                style={{ color: colors.copper }}
              >
                {QUIZ_SUBTITLE}
              </p>
              <h1
                className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6"
                style={{ color: colors.nearBlack, letterSpacing: '-0.02em' }}
              >
                {QUIZ_TITLE}
              </h1>
              <p className="text-lg text-gray-600 mb-3 max-w-lg mx-auto">
                8 quick questions. Under 90 seconds. No wrong answers.
              </p>
              <p className="text-gray-500 mb-10 max-w-lg mx-auto leading-relaxed">
                This isn&rsquo;t a home inspection checklist &mdash; it&rsquo;s a self-assessment
                that reveals how you really think about maintaining your home.
              </p>
              <button
                onClick={handleStart}
                className="group inline-flex items-center gap-3 px-10 py-5 text-lg font-medium text-white rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: colors.teal }}
              >
                Discover Your Archetype
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="mt-6 text-sm text-gray-400">
                Takes under 90 seconds &bull; No email required to start
              </p>
            </motion.div>
          )}

          {/* ─── STEP 2: QUESTIONS ─── */}
          {step === 'questions' && currentQuestion && (
            <motion.div
              key="questions-wrapper"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto px-6 pt-4 pb-16"
            >
              {/* Progress bar */}
              <div className="mb-10">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-500">
                    Question {questionIndex + 1} of {totalQuestions}
                  </span>
                  <span className="text-sm text-gray-400">
                    {Math.round(((questionIndex + 1) / totalQuestions) * 100)}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: colors.teal }}
                    initial={{ width: 0 }}
                    animate={{
                      width: `${((questionIndex + 1) / totalQuestions) * 100}%`,
                    }}
                    transition={{ duration: 0.7, ease }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentQuestion.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease }}
                >
                  <p
                    className="text-sm uppercase tracking-[0.15em] font-medium mb-2"
                    style={{ color: colors.copper }}
                  >
                    {currentQuestion.title}
                  </p>
                  <h2
                    className="font-serif text-2xl sm:text-3xl mb-8 leading-snug"
                    style={{ color: colors.nearBlack }}
                  >
                    {currentQuestion.subtitle}
                  </h2>

                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => {
                      const isSelected = answers[currentQuestion.id] === option.points
                      return (
                        <motion.button
                          key={idx}
                          type="button"
                          onClick={() =>
                            handleSelectOption(currentQuestion.id, option.points)
                          }
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.12, duration: 0.3, ease }}
                          className="w-full text-left px-6 py-5 rounded-xl border-2 transition-all duration-200 cursor-pointer"
                          style={{
                            borderColor: isSelected ? colors.teal : '#e5e7eb',
                            backgroundColor: isSelected ? `${colors.teal}08` : '#ffffff',
                          }}
                          whileHover={{
                            borderColor: colors.teal,
                            scale: 1.01,
                          }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <span
                            className="text-base font-medium"
                            style={{
                              color: isSelected ? colors.teal : colors.nearBlack,
                            }}
                          >
                            {option.text}
                          </span>
                        </motion.button>
                      )
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {/* ─── STEP 3: LEAD CAPTURE ─── */}
          {step === 'lead' && (
            <motion.div
              key="lead"
              {...fadeUp}
              transition={{ duration: 0.7, ease }}
              className="max-w-md mx-auto px-6 py-16 lg:py-24"
            >
              <div className="text-center mb-8">
                <div
                  className="w-14 h-14 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${colors.teal}12` }}
                >
                  <svg
                    className="w-7 h-7"
                    style={{ color: colors.teal }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2
                  className="font-serif text-2xl sm:text-3xl mb-3"
                  style={{ color: colors.nearBlack }}
                >
                  Almost there!
                </h2>
                <p className="text-gray-500">
                  Where should we send your results?
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Results shown immediately either way. Email gets a detailed report.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <label className="block">
                  <span className="text-sm font-medium text-gray-600">Name</span>
                  <input
                    type="text"
                    value={lead.name}
                    onChange={(e) =>
                      setLead((p) => ({ ...p, name: e.target.value }))
                    }
                    placeholder="Your name"
                    className="mt-1 block w-full px-4 py-3.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:outline-none transition-shadow"
                    style={{
                      focusRingColor: colors.teal,
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = colors.teal
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}20`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-600">Email</span>
                  <input
                    type="email"
                    value={lead.email}
                    onChange={(e) =>
                      setLead((p) => ({ ...p, email: e.target.value }))
                    }
                    placeholder="you@example.com"
                    className="mt-1 block w-full px-4 py-3.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:outline-none transition-shadow"
                    aria-invalid={lead.email.trim() !== '' && !isEmailValid}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = colors.teal
                      e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}20`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                  {lead.email.trim() !== '' && !isEmailValid && (
                    <p className="mt-2 text-sm text-black">Please enter a valid email address.</p>
                  )}
                </label>
              </div>

              <button
                onClick={handleLeadSubmit}
                disabled={!canSubmit}
                className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: canSubmit ? colors.teal : '#9CA3AF' }}
              >
                See My Results
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          )}

          {/* ─── STEP 4: CALCULATING ─── */}
          {step === 'calculating' && (
            <motion.div
              key="calculating"
              {...fadeUp}
              transition={{ duration: 0.7, ease }}
              className="max-w-md mx-auto px-6 py-32 text-center"
            >
              <motion.div
                className="w-20 h-20 rounded-full mx-auto mb-8 flex items-center justify-center"
                style={{ backgroundColor: `${colors.teal}15` }}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Loader2
                  className="w-10 h-10 animate-spin"
                  style={{ color: colors.teal }}
                />
              </motion.div>
              <h2
                className="font-serif text-2xl sm:text-3xl mb-3"
                style={{ color: colors.nearBlack }}
              >
                Analyzing your responses&hellip;
              </h2>
              <p className="text-gray-500">
                Discovering your home maintenance personality
              </p>
              <div className="mt-8 flex justify-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: colors.teal }}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* ─── STEP 5: RESULTS ─── */}
          {step === 'results' && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
            >
              <ArchetypeReport
                archetype={archetype}
                protectionScore={protectionScore}
                rawScore={rawScore}
                leadName={lead.name || undefined}
                leadEmail={lead.email || undefined}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {step === 'results' && <Footer />}
    </div>
  )
}

export default QuizFunnelPage
